import { Fragment } from 'react';
import parse from 'html-react-parser';
import type { BasisStructure } from 'types/cms';
import styles from './autonative-article-detail.module.scss';

export type TagName = 'bold' | 'strong' | 'em' | 'u' | 'strike' | 'sub' | 'sup' | 'inline_link';

type AutonativeArticleAnnotationsType = {
  readonly value: string;
  readonly annotations: ReadonlyArray<{
    readonly index: number;
    readonly length: number;
    readonly name: TagName;
    readonly value: string;
    readonly newWindow?: string;
  }>;
};

type AutonativeArticleDetailFields = {
  readonly level: string;
  readonly value: string;
  readonly title: string;
  readonly body: string;
  readonly headline?: AutonativeArticleAnnotationsType;
  readonly subtitle?: AutonativeArticleAnnotationsType;
  readonly paragraph?: AutonativeArticleAnnotationsType;
};

export type ItemAutonativeArticleDetailDefaultProps = {
  readonly id: string;
  readonly type: string;
  readonly model: {
    readonly createdAt: string;
    readonly publishedAt: string;
    readonly homeSection: string;
    readonly id: string;
    readonly type: string;
    readonly fields: AutonativeArticleDetailFields;
    readonly title: string;
    readonly lastmodifiedAt: string;
    readonly url: string;
  };
};

export interface WidgetArticleDetailDefaultProps extends BasisStructure {
  readonly items: ReadonlyArray<ItemAutonativeArticleDetailDefaultProps>;
}

type StartAnnotationProps = {
  readonly pos?: number;
  readonly name: TagName;
  readonly value?: string;
  readonly target?: string;
};

type EndAnnotationProps = {
  readonly pos?: number;
  readonly name: TagName;
};

const getTag = (tagName: TagName) => {
  switch (tagName) {
    case 'bold':
    case 'strong':
      return 'strong';
    case 'em':
      return 'em';
    case 'u':
      return 'u';
    case 'strike':
      return 'strike';
    case 'sub':
      return 'sub';
    case 'sup':
      return 'sup';
    default:
      return;
  }
};

const annotationConvert = (annotations: AutonativeArticleAnnotationsType) => {
  if (annotations.annotations && annotations.annotations.length === 0) {
    return annotations.value;
  }

  let annotated = '';

  const startAnnotations: Array<StartAnnotationProps> = [];
  let endAnnotations: Array<EndAnnotationProps> = [];

  annotations.annotations.forEach((a) => {
    if (a.name === 'inline_link') {
      startAnnotations.push({
        pos: a.index,
        name: a.name,
        value: a.value,
        target: a.newWindow ? '_blank' : '_self',
      });
    } else {
      startAnnotations.push({ pos: a.index, name: a.name });
    }
    endAnnotations.push({ pos: a.index + a.length, name: a.name });
  });
  endAnnotations = endAnnotations.reverse();

  for (let i = 0; i < annotations.value.length; i++) {
    endAnnotations
      .filter((a) => a.pos === i)
      .forEach((annotation) => {
        annotated = annotated + `</${getTag(annotation.name)}>`;
      });
    startAnnotations
      .filter((a) => a.pos === i)
      .forEach((annotation) => {
        if (annotation.name === 'inline_link') {
          annotated = annotated + `<${getTag(annotation.name)} href=${annotation.value} target=${annotation.target}>`;
        } else {
          annotated = annotated + `<${getTag(annotation.name)}>`;
        }
      });
    annotated = annotated + annotations.value.charAt(i);
  }
  return annotated;
};

const AutonativeArticleDetailDefault = ({ context }: { context: WidgetArticleDetailDefaultProps }) => {
  const printTypeElements = (item: ItemAutonativeArticleDetailDefaultProps) => {
    if (item.type === 'storyline_headline') {
      return <h2 className={styles.headline}>{item.model.fields.headline?.value}</h2>;
    }

    if (item.type === 'storyline_subheadline') {
      return <h3 className={styles.subHeadline}>{item.model.fields.headline?.value}</h3>;
    }

    if (item.type === 'storyline_paragraph' && item.model.fields.paragraph?.annotations) {
      return <p className={styles.paragraph}>{parse(annotationConvert(item.model.fields.paragraph))}</p>;
    }

    return null;
  };
  return context.items?.map((item) => <Fragment key={item.id}>{printTypeElements(item)}</Fragment>);
};
export default AutonativeArticleDetailDefault;
